import React, { useCallback, useState, useMemo, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { getYear, getMonth, getQuarter, format } from 'date-fns';
import Text from 'design-system/Text';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import Modal from 'utils/packages/Modal';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useDisableAndroidBackButton } from 'hooks';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import Controller from './Controller';
import { isWeb } from '../../constants';
import { DateFilterContext } from './context';
import { getYearArrArr } from '../analyticsFilter';
import { filterStartYear, options } from './constants';
const currentYear = getYear(new Date());
const [weekly, monthly, quarterly, yearly, custom] = options;
const getTitle = (period) => {
    switch (period) {
        case weekly:
            return 'weekly';
        case monthly:
            return 'monthly';
        case quarterly:
            return 'quarterly';
        case yearly:
            return 'yearly';
        case custom:
            return 'period';
        default: {
            return '';
        }
    }
};
const DateFilter = ({ onSave, period }) => {
    const styles = useStyles(modalStyleSet);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    const currentStates = useMemo(() => ({
        currentYear,
        currentMonth: getMonth(new Date()),
        currentQuarter: getQuarter(new Date()),
        todayDateStr: format(new Date(), 'yyyy-MM-dd'),
    }), []);
    const { yearData, defaultYearIndex } = getYearArrArr(filterStartYear, currentYear, currentYear);
    // weekly
    const [weeklyRange, setWeeklyRange] = useState();
    // monthly
    const [selectedYear, setSelectedYear] = useState(currentStates.currentYear);
    const [selectedMonth, setSelectedMonth] = useState(currentStates.currentMonth);
    // quarterly
    const [selectedQuarter, setSelectedQuarter] = useState(currentStates.currentQuarter);
    // yearly
    const [selectedYearFilter, setSelectedYearFilter] = useState(currentStates.currentYear);
    const [selectedYearFilterIndex, setSelectedYearFilterIndex] = useState(defaultYearIndex);
    // custom
    const [to, setTo] = useState();
    const [from, setFrom] = useState();
    const contextValue = useMemo(() => ({
        period,
        ...currentStates,
        // week
        weeklyRange,
        setWeeklyRange,
        // month
        selectedYear,
        selectedMonth,
        setSelectedYear,
        setSelectedMonth,
        // quarter
        selectedQuarter,
        setSelectedQuarter,
        // year
        yearData,
        defaultYearIndex,
        selectedYearFilter,
        setSelectedYearFilter,
        selectedYearFilterIndex,
        setSelectedYearFilterIndex,
        to,
        from,
        setTo,
        setFrom,
    }), [
        currentStates,
        defaultYearIndex,
        from,
        period,
        selectedMonth,
        selectedQuarter,
        selectedYear,
        selectedYearFilter,
        selectedYearFilterIndex,
        to,
        weeklyRange,
        yearData,
    ]);
    const handleSave = useCallback(() => {
        switch (period) {
            case weekly:
                if (weeklyRange) {
                    onSave({
                        from: weeklyRange[0],
                        to: weeklyRange[1],
                        type: 'Weekly',
                    });
                }
                break;
            case monthly:
                if (selectedMonth !== undefined) {
                    onSave({
                        month: selectedMonth,
                        year: selectedYear,
                        type: 'Monthly',
                    });
                }
                break;
            case quarterly:
                if (selectedQuarter !== undefined) {
                    onSave({
                        quarter: selectedQuarter,
                        year: selectedYear,
                        type: 'Quarterly',
                    });
                }
                break;
            case yearly:
                if (selectedYearFilter !== undefined) {
                    onSave({ year: selectedYearFilter, type: 'Yearly' });
                }
                break;
            case custom:
                if (from) {
                    onSave({ from, to, type: 'Custom' });
                }
                break;
            default:
                break;
        }
    }, [
        from,
        onSave,
        period,
        selectedMonth,
        selectedQuarter,
        selectedYear,
        selectedYearFilter,
        to,
        weeklyRange,
    ]);
    const title = useMemo(() => getTitle(period), [period]);
    return (<DateFilterContext.Provider value={contextValue}>
      <View style={paddingBottom}>
        {!isWeb && (<View style={styles.titleContainer}>
            <Text flex Text-20 centered>
              Filter {title}
            </Text>
          </View>)}
        <Controller />
        <View style={styles.buttonContainer}>
          <Button brand onPress={handleSave} title="Save"/>
        </View>
      </View>
    </DateFilterContext.Provider>);
};
const useDateFilter = () => {
    const isVisible = useRef(false);
    const handleModalHide = useCallback(() => {
        isVisible.current = false;
    }, []);
    const hide = useCallback(() => {
        Modal.hide();
    }, []);
    const show = useRequiredBenefitCallbackWithSpaces(useCallback((onSave, period) => {
        isVisible.current = true;
        Modal.show(<DateFilter onSave={onSave} period={period}/>, {
            containerStyle: styles.modal,
            backgroundLight: true,
            title: isWeb ? (<View style={styles.titleContainer}>
              <Text flex Text-20>
                Filter {getTitle(period)}
              </Text>
            </View>) : undefined,
            titleBottomBorder: isWeb,
            onDidHideCallback: handleModalHide,
        });
    }, [handleModalHide]), 'search');
    const handleModalBackPress = useCallback(() => {
        if (isVisible.current) {
            hide();
        }
        return isVisible.current;
    }, [hide]);
    useDisableAndroidBackButton(handleModalBackPress);
    return [show, hide];
};
export default useDateFilter;
const styles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        margin: 0,
        paddingBottom: 0,
    },
    titleContainer: {
        justifyContent: 'center',
    },
});
const modalStyleSet = createStyleSheets((colors) => ({
    container: {
        backgroundColor: colors.background.light,
    },
    titleContainer: {
        paddingTop: rem(8),
        paddingHorizontal: rem(16),
        marginTop: rem(26),
    },
    resetButton: {
        alignSelf: 'center',
        marginTop: rem(8),
        marginBottom: rem(16),
    },
    resetButtonCompact: {
        marginBottom: rem(8),
    },
    buttonContainer: {
        marginHorizontal: rem(16),
        marginTop: rem(8),
    },
}));
